import {
  NoPreloading,
  Routes,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig
} from '@angular/router';
import { inject, provideEnvironmentInitializer } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { authGuard } from '@core/auth-guard';
import { permissionGuard } from '@core/permission-guard';
import { PERMISSION_KEYS } from '@modules/shared/constants/permissions.contant';
import { ROUTE_PATHS } from '@constants/routes';
import { whitelabelV2Guard } from '@core/whitelabel-v2.guard';
import { optOutRedirectGuard } from '@core/opt-out-redirect.guard';
import { creatorApprovalGuard } from '@core/creator-approval.guard';
import { wlsOwnerGuard } from '@core/wls-owner.guard';
import { SIDEBAR_TABS } from '@shared/constants/tab-approval.constant';
import { MARKETING_PAGE_ROUTES, MARKETING_PAGE_SLUGS } from '@constants/marketing-page-routes.generated';
import { getMarketingPageRedirectRoutes } from '@constants/marketing-page-redirects';
import { COMPARISON_PAGE_ROUTES } from '@constants/comparison-page-routes.generated';
import { MARKETING_PRO_PAGE_ROUTES } from '@constants/marketing-pro-page-routes.generated';
import { withCaseInsensitivePaths } from '@core/case-insensitive-url.matcher';
import { whiteLabelRootSubPageMatcher } from '@modules/sub-page-view/sub-page-view.constants';
import { whiteLabelRootSubPageCanMatch } from '@modules/sub-page-view/white-label-root-sub-page.can-match';
import { pwaMarketingRedirectGuard } from '@core/pwa-marketing-redirect.guard';
import { PUBLIC_PAGE_ROUTE_DATA } from '@constants/public-page-route-data';

const permissionKeys = PERMISSION_KEYS;
const marketingSurfaceGuards = [whitelabelV2Guard, pwaMarketingRedirectGuard];
const withWhitelabelGuard = (routes: Routes): Routes =>
  withCaseInsensitivePaths(routes).map((route) =>
    route.redirectTo
      ? route
      : {
          ...route,
          canActivate: marketingSurfaceGuards
        }
  );
const marketingPageRoutes: Routes = withWhitelabelGuard([
  ...getMarketingPageRedirectRoutes(MARKETING_PAGE_SLUGS),
  ...MARKETING_PAGE_ROUTES
]);
const comparisonPageRoutes: Routes = withWhitelabelGuard(COMPARISON_PAGE_ROUTES);
const marketingProPageRoutes: Routes = withWhitelabelGuard(MARKETING_PRO_PAGE_ROUTES);
const routes: Routes = [
  {
    path: ROUTE_PATHS.AUTH,
    loadChildren: () =>
      import(/* webpackChunkName: "auth" */ './modules/auth/auth.routes').then((m) => m.AUTH_ROUTES),
    canActivate: [authGuard]
  },
  {
    path: ROUTE_PATHS.BIO,
    loadChildren: () =>
      import('./modules/link-in-bio-fan-preview/link-in-bio-fan-preview.routes').then(
        (m) => m.LINK_IN_BIO_FAN_PREVIEW_ROUTES
      )
  },
  {
    path: ROUTE_PATHS.DASHBOARD,
    loadChildren: () =>
      import('./modules/dashboard/dashboard.routes').then(
        (m) => m.DASHBOARD_ROUTES
      )
  },
  {
    path: ROUTE_PATHS.FORUM,
    loadChildren: () =>
      import('./modules/forum/forum.routes').then((m) => m.FORUM_ROUTES),
    canActivate: [authGuard, permissionGuard],
    data: {
      delegatePath: [permissionKeys.MAKE_EDIT_CHAT_ROOM_POSTS]
    }
  },
  {
    path: ROUTE_PATHS.MONETIZE,
    loadChildren: () =>
      import('./modules/monetize/monetize.routes').then(
        (m) => m.MONETIZE_ROUTES
      ),
    canActivate: [authGuard, permissionGuard, creatorApprovalGuard, wlsOwnerGuard],
    data: {
      path: permissionKeys.MONETIZE,
      tabKey: SIDEBAR_TABS.MONETIZE
    }
  },
  {
    path: ROUTE_PATHS.EDIT_PREFERENCE,
    loadChildren: () =>
      import('./modules/edit-preference/edit-preference.routes').then(
        (m) => m.EDIT_PREFERENCE_ROUTES
      ),
    canActivate: [authGuard, permissionGuard],
    data: {
      delegatePath: [permissionKeys.EDIT_MY_PREFERENCES]
    }
  },
  {
    path: ROUTE_PATHS.OPT_OUT,
    children: [],
    canActivate: [optOutRedirectGuard]
  },
  {
    path: ROUTE_PATHS.CONTENT,
    loadChildren: () =>
      import('./modules/setup-profile/setup-profile.routes').then(
        (m) => m.SETUP_PROFILE_ROUTES
      ),
    canActivate: [authGuard]
  },
  {
    path: ROUTE_PATHS.POST,
    loadChildren: () =>
      import('./modules/post-detail/post-detail.routes').then(
        (m) => m.POST_DETAIL_ROUTES
      )
  },
  // {
  //   path: ROUTE_PATHS.EXPLORE_CREATORS_ROUTE,
  //   loadComponent: () =>
  //     import(
  //       './modules/creators/components/creator-container/creator-container.component'
  //     ).then((m) => m.CreatorContainerComponent),
  //   canActivate: [authGuard, permissionGuard, creatorApprovalGuard],
  //   data: {
  //     path: permissionKeys.CREATORS,
  //     tabKey: SIDEBAR_TABS.CREATORS
  //   }
  // },
  {
    path: ROUTE_PATHS.WALLET,
    loadChildren: () =>
      import('./modules/wallet/wallet.routes').then((m) => m.WALLET_ROUTES),
    canActivate: [authGuard, permissionGuard, creatorApprovalGuard],
    data: {
      path: permissionKeys.WALLET,
      tabKey: SIDEBAR_TABS.WALLET
    }
  },
  {
    path: ROUTE_PATHS.ORDER_STATUS,
    loadChildren: () =>
      import(
        './modules/public/fulfillment-order/fulfillment-order.routes'
      ).then((m) => m.FULFILLMENT_ORDER_ROUTES)
  },
  {
    path: ROUTE_PATHS.MESSAGES,
    loadChildren: () =>
      import('./modules/messages/messages.routes').then(
        (m) => m.MESSAGES_ROUTES
      ),
    canActivate: [authGuard, permissionGuard, creatorApprovalGuard],
    data: {
      tabKey: SIDEBAR_TABS.MESSAGES,
      delegatePath: [permissionKeys.SEND_REPLY_TO_DIRECT_MESSAGES]
    }
  },
  {
    path: ROUTE_PATHS.INSIGHTS,
    loadChildren: () =>
      import('./modules/insights/insights.routes').then(
        (m) => m.INSIGHTS_ROUTES
      ),
    canActivate: [authGuard, permissionGuard, creatorApprovalGuard, wlsOwnerGuard],
    data: {
      path: permissionKeys.INSIGHTS,
      tabKey: SIDEBAR_TABS.INSIGHTS,
      delegatePath: [
        permissionKeys.ACCESS_USER_SEGMENTATION,
        permissionKeys.VIEW_EARNING_METRICS,
        permissionKeys.VIEW_ENGAGEMENT_METRICS,
        permissionKeys.FAN_MANAGEMENT
      ]
    }
  },
  {
    path: ROUTE_PATHS.PRIVACY_POLICY,
    loadComponent: () =>
      import('./modules/public/privacy-policy/privacy-policy.component').then(
        (m) => m.PrivacyPolicyComponent
      ),
    data: PUBLIC_PAGE_ROUTE_DATA.data.PRIVACY_POLICY
  },
  {
    path: ROUTE_PATHS.CREATOR,
    loadComponent: () =>
      import('./modules/creators-home/creators-home.component').then(
        (m) => m.CreatorsHomeComponent
      ),
    canActivate: marketingSurfaceGuards
  },
  {
    path: ROUTE_PATHS.TERMS_CONDITIONS,
    loadComponent: () =>
      import('./modules/public/terms-condition/terms-condition.component').then(
        (m) => m.TermsConditionComponent
      ),
    title: PUBLIC_PAGE_ROUTE_DATA.titles.TERMS_CONDITIONS
  },
  {
    path: ROUTE_PATHS.CONTACT_US,
    loadComponent: () =>
      import('./modules/public/contact-us/contact-us/contact-us.component').then(
        (m) => m.ContactUsComponent
      ),
    canActivate: [pwaMarketingRedirectGuard],
    data: PUBLIC_PAGE_ROUTE_DATA.data.CONTACT
  },
  {
    path: ROUTE_PATHS.REQUEST_DEMO,
    loadComponent: () =>
      import('./modules/public/contact-us/contact-v1/contact-v1.component').then(
        (m) => m.ContactV1Component
      ),
    canActivate: [pwaMarketingRedirectGuard],
    data: PUBLIC_PAGE_ROUTE_DATA.data.CONTACT
  },
  {
    path: ROUTE_PATHS.SCHEDULE_A_DEMO,
    loadComponent: () =>
      import('./modules/public/contact-us/contact-v1/contact-v1.component').then(
        (m) => m.ContactV1Component
      ),
    canActivate: [pwaMarketingRedirectGuard],
    data: PUBLIC_PAGE_ROUTE_DATA.data.CONTACT
  },
  {
    path: ROUTE_PATHS.UNSUBSCRIBE_INSTRUCTION,
    loadComponent: () =>
      import(
        './modules/public/unsubscribe-instruction/unsubscribe-instruction.component'
      ).then((m) => m.UnsubscribeInstructionComponent)
  },
  {
    path: ROUTE_PATHS.NEWSLETTER_UNSUBSCRIBE,
    loadComponent: () =>
      import('./modules/public/unsubscribe-newsletter/unsubscribe-newsletter.component').then((m) => m.UnsubscribeNewsletterComponent)
  },
  {
    path: ROUTE_PATHS.IMPORTED_OPT_OUT,
    loadComponent: () =>
      import('./modules/public/imported-opt-out/imported-opt-out.component').then((m) => m.ImportedOptOutComponent)
  },
  {
    path: ROUTE_PATHS.ABOUT,
    loadComponent: () =>
      import('./modules/public/about/about.component').then((m) => m.AboutComponent),
    canActivate: marketingSurfaceGuards,
    data: PUBLIC_PAGE_ROUTE_DATA.data.ABOUT
  },
  {
    path: ROUTE_PATHS.PRICING,
    loadComponent: () =>
      import('./modules/public/pricing/pricing.component').then(
        (m) => m.PricingComponent
      ),
    canActivate: marketingSurfaceGuards,
    data: PUBLIC_PAGE_ROUTE_DATA.data.PRICING
  },
  {
    path: ROUTE_PATHS.SHOPIFY_CONNECT,
    loadComponent: () =>
      import('./modules/public/shopify-connect/shopify-connect.component').then(
        (m) => m.ShopifyConnectComponent
      )
  },
  {
    path: ROUTE_PATHS.CALENDAR_CONNECT,
    loadComponent: () =>
      import('./modules/public/calendar-connect/calendar-connect.component').then(
        (m) => m.CalendarConnectComponent
      )
  },
  {
    path: ROUTE_PATHS.ADVERTISE,
    loadComponent: () =>
      import('./modules/advertisement-home/advertisement-home.component').then(
        (m) => m.AdvertisementHomeComponent
      ),
    canActivate: marketingSurfaceGuards
  },
  {
    path: ROUTE_PATHS.GLOBAL,
    loadChildren: () =>
      import('./modules/global/global.routes').then((m) => m.GLOBAL_ROUTES)
  },
  {
    path: ROUTE_PATHS.TERMS_OF_SERVICE,
    loadComponent: () =>
      import('./modules/public/terms-condition/terms-condition.component').then(
        (m) => m.TermsConditionComponent
      ),
    title: PUBLIC_PAGE_ROUTE_DATA.titles.TERMS_CONDITIONS
  },
  ...comparisonPageRoutes,
  ...marketingProPageRoutes,
  {
    path: ROUTE_PATHS.HELP_FAQ,
    loadComponent: () =>
      import('./modules/promotional/help-faq/help-faq.component').then(
        (m) => m.HelpFaqComponent
      ),
    canActivate: [pwaMarketingRedirectGuard],
    data: PUBLIC_PAGE_ROUTE_DATA.data.HELP_FAQ
  },
  {
    path: ROUTE_PATHS.SHARE,
    loadComponent: () =>
      import('./modules/public/shortend-url/shortend-url.component').then(
        (m) => m.ShortendUrlComponent
      )
  },
  {
    path: ROUTE_PATHS.ACCESS_USER,
    loadComponent: () =>
      import(
        './modules/public/access-user-account/access-user-account.component'
      ).then((m) => m.AccessUserAccount)
  },
  {
    path: ROUTE_PATHS.RESOURCES,
    loadChildren: () =>
      import('./modules/resources/resources.routes').then(
        (m) => m.RESOURCES_ROUTES
      ),
    canActivate: [authGuard, permissionGuard, creatorApprovalGuard, wlsOwnerGuard],
    data: {
      path: permissionKeys.RESOURCES,
      tabKey: SIDEBAR_TABS.RESOURCES
    }
  },
  {
    path: ROUTE_PATHS.CREATORS,
    loadComponent: () =>
      import('./modules/creators-home/creators-home.component').then(
        (m) => m.CreatorsHomeComponent
      ),
    canActivate: marketingSurfaceGuards
  },
  {
    path: ROUTE_PATHS.NOT_FOUND,
    loadComponent: () =>
      import('./modules/public/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      )
  },
  ...marketingPageRoutes,
  {
    path: 'wls/:id',
    loadChildren: () =>
      import('./modules/profile/profile.routes').then((m) => m.PROFILE_ROUTES)
  },
  {
    matcher: whiteLabelRootSubPageMatcher,
    canMatch: [whiteLabelRootSubPageCanMatch],
    loadComponent: () =>
      import('./modules/sub-page-view/sub-page-view.component').then(
        (m) => m.SubPageViewComponent
      )
  },
  {
    path: ROUTE_PATHS.PROFILE_SUB_PAGE,
    loadComponent: () =>
      import('./modules/sub-page-view/sub-page-view.component').then(
        (m) => m.SubPageViewComponent
      )
  },
  {
    path: ROUTE_PATHS.PROFILE,
    loadChildren: () =>
      import('./modules/profile/profile.routes').then((m) => m.PROFILE_ROUTES)
  },
  {
    path: ROUTE_PATHS.HOME,
    loadChildren: () =>
      import('./modules/promotional/promotional.routes').then(
        (m) => m.PROMOTIONAL_ROUTES
      ),
    canActivate: [pwaMarketingRedirectGuard]
  },
  {
    path: ROUTE_PATHS.WILDCARD,
    loadComponent: () =>
      import('./modules/public/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      )
  }
];

export const appRouterProviders = [
  provideRouter(
    routes,
    withEnabledBlockingInitialNavigation(),
    withPreloading(NoPreloading),
    withInMemoryScrolling({
      scrollPositionRestoration: 'top',
      anchorScrolling: 'enabled'
    }),
    withRouterConfig({
      onSameUrlNavigation: 'reload',
      paramsInheritanceStrategy: 'always',
      urlUpdateStrategy: 'deferred'
    })
  ),
  provideEnvironmentInitializer(() => {
    inject(ViewportScroller).setOffset([0, 64]);
  })
];
